import { createDraftSafeSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@src/redux/store';
import { Campaign, ChannelType } from '@src/types/campaign';
import { InfluencerToSend, InfluencerExcelData } from '@src/types/influencer';
import {
  assignInfluencerToCampaignThunk,
  getCampaignInfluencerListThunk,
  getCampaignThunk,
  updateCampaignThunk,
} from './campaignThunk';
import { ModalType } from '../types';

interface CampaignState {
  isLoading: boolean;
  campaign: Campaign;
  influencers: InfluencerToSend[];
  modal: ModalType[];
}

const campaignInitialState: CampaignState = {
  isLoading: false,
  campaign: {
    id: 0,
    charisCampaignId: '',
    status: 'pending',
    country: 'indonesia',
    recruitInfluencer: 0,
    channelType: 'instagram',
    postingType: 'picture2',
    followerGrade: 'group1-1',
    copyright: 'none',
    hopeDate: '',
    contentEssentials: '',
    contentEssentialsEn: '',
    pointText: '',
    pointTextEn: '',
    hashTags: [],
    accountHashTags: [],
    purchaseUrl: '',
    voucherCode: '',
    allowedRetrieveInfluencers: false,
  },
  influencers: [],
  modal: [],
};

const campaignSlice = createSlice({
  name: 'campaign',
  initialState: campaignInitialState,
  reducers: {
    onChangeGetInfluencer: (state, { payload }) => {
      const {
        channelType,
        data,
      }: { channelType: ChannelType; data: InfluencerExcelData[] } = payload;
      const _channelType =
        channelType.charAt(0).toUpperCase() + channelType.slice(1);

      const packagedData: InfluencerToSend[] = data
        .filter((element) => element.진행상태 === '신청')
        .map((element) => {
          return {
            charisSaId: element.캠페인신청ID,
            charisUserId: element.UID,
            influencerName: element.신청자,
            accountName: element[`${_channelType} Username`],
            accountUrl: element[`${_channelType} URL`],
            follower: element[`${_channelType} Follow #`],
          };
        });

      state.influencers = packagedData;
    },
    onChangeDeleteInfluencer: (state, { payload }) => {
      state.influencers = state.influencers.filter(
        (element) => element.charisSaId !== payload
      );
    },
    onChangeInfluencerApprove: (state, { payload }) => {
      state.influencers.map((el) => {
        if (el.charisUserId === payload.charisUserId) {
          el.approved = true;

          return el;
        }

        return el;
      });
    },
    onChangeModal: (state, { payload }) => {
      if (payload === 'off') {
        state.modal = [];
      } else if (state.modal.includes(payload)) {
        state.modal = state.modal.filter((modal) => modal !== payload);
      } else {
        state.modal = [...state.modal, payload];
      }
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getCampaignThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(getCampaignThunk.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.campaign = payload;
      })
      .addCase(getCampaignThunk.rejected, (state) => {
        state.isLoading = false;
      });

    addCase(updateCampaignThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(updateCampaignThunk.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.modal = [];

        state.campaign = payload;
      })
      .addCase(updateCampaignThunk.rejected, (state) => {
        state.isLoading = false;
      });

    addCase(assignInfluencerToCampaignThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(assignInfluencerToCampaignThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(assignInfluencerToCampaignThunk.rejected, (state) => {
        state.isLoading = false;
      });

    addCase(getCampaignInfluencerListThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(
        getCampaignInfluencerListThunk.fulfilled,
        (state, { payload }) => {
          state.isLoading = false;
          state.influencers = payload;
        }
      )
      .addCase(getCampaignInfluencerListThunk.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  onChangeGetInfluencer,
  onChangeDeleteInfluencer,
  onChangeInfluencerApprove,
  onChangeModal,
} = campaignSlice.actions;

export default campaignSlice.reducer;

const selfSelector = (state: RootState) => state.campaign;
export const campaignSelector = createDraftSafeSelector(
  selfSelector,
  (state) => state
);
